import React from 'react';
import Icon from '../../../components/AppIcon';
import { ContactMethod, AvailabilityStatus } from '../types';

interface ContactInfoPanelProps {
  contactMethods: ContactMethod[];
  availability: AvailabilityStatus;
}

const ContactInfoPanel = ({ contactMethods, availability }: ContactInfoPanelProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'text-teal-700';
      case 'busy':
        return 'text-red-700';
      case 'limited':
        return 'text-amber-700';
      default:
        return 'text-[#0C2C39]/40';
    }
  };

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'border-teal-700';
      case 'busy':
        return 'border-red-700';
      case 'limited':
        return 'border-amber-700';
      default:
        return 'border-[#0C2C39]/10';
    }
  };

  const handleContactClick = (method: ContactMethod) => {
    switch (method.type) {
      case 'email':
        window.location.href = `mailto:${method.value}`;
        break;
      case 'phone':
        window.location.href = `tel:${method.value}`;
        break;
      case 'linkedin':
        window.open(method.value, '_blank', 'noopener,noreferrer');
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-10">
      {/* Availability Status */}
      <div className={`neubrutal-card bg-white p-8 relative overflow-hidden group`}>
        <div className="absolute top-0 right-0 w-24 h-24 bg-[#0C2C39]/5 rounded-bl-[4rem] group-hover:bg-[#0C2C39]/10 transition-colors" />

        <div className="flex items-start space-x-5 relative z-10">
          <div className={`w-4 h-4 rounded-full mt-1.5 border-2 ${getStatusBorderColor(availability.status)} animate-pulse shadow-[0_0_10px_rgba(0,0,0,0.1)]`} />
          <div className="flex-1">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/30 mb-2">Live Availability</h3>
            <p className={`text-xl font-black mb-3 tracking-tight ${getStatusColor(availability.status)}`}>
              {availability.message}
            </p>
            <div className="space-y-4 pt-4 border-t-2 border-[#0C2C39]/5">
              <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                <span className="text-[#0C2C39]/40">Latency</span>
                <span className="text-[#0C2C39]">{availability.responseTime}</span>
              </div>
              {availability.nextAvailable && (
                <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest">
                  <span className="text-[#0C2C39]/40">Next Opening</span>
                  <span className="text-teal-700">{availability.nextAvailable}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alternative Contact methods redesigned as a clean list */}
      <div className="neubrutal-card bg-white p-8">
        <h3 className="text-sm font-black text-[#0C2C39] uppercase tracking-[0.2em] mb-8 pb-4 border-b-2 border-[#0C2C39]/5">
          Reach Out
        </h3>
        <div className="space-y-6">
          {contactMethods.map((method) => (
            <div
              key={method.id}
              onClick={() => handleContactClick(method)}
              className="flex items-center space-x-5 group cursor-pointer"
            >
              <div className="w-12 h-12 bg-[#0C2C39]/5 border-2 border-[#0C2C39]/10 rounded-xl flex items-center justify-center group-hover:bg-[#0C2C39] group-hover:text-white group-hover:border-[#0C2C39] transition-all duration-300">
                <Icon name={method.icon as any} size={20} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/40 mb-1">{method.label}</p>
                <p className="font-black text-[#0C2C39] text-sm group-hover:text-teal-700 transition-colors uppercase tracking-tight">{method.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Location card */}
      <div className="neubrutal-card bg-white p-8 bg-teal-700/5 border-dashed border-[#0C2C39]/20">
        <h3 className="text-[10px] font-black text-[#0C2C39] uppercase tracking-widest mb-6">HQ Orientation</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-white border-2 border-[#0C2C39] rounded-lg flex items-center justify-center text-[#0C2C39]">
              <Icon name="MapPin" size={18} />
            </div>
            <span className="text-sm font-black text-[#0C2C39] uppercase tracking-tight">San Francisco, USA</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-white border-2 border-[#0C2C39] rounded-lg flex items-center justify-center text-[#0C2C39]">
              <Icon name="Clock" size={18} />
            </div>
            <span className="text-sm font-black text-[#0C2C39] uppercase tracking-tight">Pacific Time (UTC-8)</span>
          </div>
        </div>
      </div>

      {/* Communication styles */}
      <div className="neubrutal-card bg-[#0C2C39] p-8 text-white shadow-[6px_6px_0px_0px_rgba(13,148,136,0.3)]">
        <h3 className="text-[10px] font-black uppercase tracking-[0.3em] mb-8 text-teal-400">Protocol Settings</h3>
        <div className="space-y-5">
          {[
            'Email for deep project logic',
            'Video calls for initialization',
            'Slack for continuous flow',
            'Voice for priority alerts'
          ].map((text, i) => (
            <div key={i} className="flex items-start space-x-3 group">
              <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 group-hover:scale-150 transition-transform" />
              <span className="text-[10px] font-black uppercase tracking-widest text-teal-400/80 group-hover:text-white transition-colors">
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactInfoPanel;